import {
  DOCUMENT_STATUS,
  SUBSCRIPTION_STATUS,
  SUPPORT_TICKET_STATUS,
  VERIFICATION_STATUS,
  type DocumentStatus,
  type SubscriptionStatus,
  type SupportTicketStatus,
  type VerificationStatus,
} from '@/contracts';

/**
 * The four tints the prototype's `statusTint()` helper resolves to.
 *
 * The prototype keyed that map on the Arabic label, which works for a static HTML file
 * and breaks the moment a second locale exists (ADR-013): the pill would lose its colour
 * in English. Here the key is the domain status from `@/contracts` — the same value
 * Prisma stores and the API returns — and the label is looked up separately from the
 * message catalog. Colour and text are now independent of each other, which is what lets
 * `en` render correctly without a second copy of this table.
 */
export type StatusTone = 'approved' | 'pending' | 'rejected' | 'neutral';

export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  approved: 'bg-pitch-50 text-pitch-700',
  pending: 'bg-gold-50 text-gold-700',
  rejected: 'bg-rose-50 text-rose-600',
  neutral: 'bg-conc-50 text-conc-500',
};

const VERIFICATION_TONE: Record<VerificationStatus, StatusTone> = {
  [VERIFICATION_STATUS.VERIFIED]: 'approved',
  [VERIFICATION_STATUS.PENDING]: 'pending',
  [VERIFICATION_STATUS.REJECTED]: 'rejected',
  [VERIFICATION_STATUS.UNVERIFIED]: 'neutral',
};

const DOCUMENT_TONE: Record<DocumentStatus, StatusTone> = {
  [DOCUMENT_STATUS.APPROVED]: 'approved',
  [DOCUMENT_STATUS.AWAITING_AUDIT]: 'pending',
  [DOCUMENT_STATUS.SCANNING]: 'pending',
  [DOCUMENT_STATUS.PENDING]: 'neutral',
  [DOCUMENT_STATUS.REJECTED]: 'rejected',
  // An infected upload is a rejection with a security incident attached to it. Never
  // let it read as merely "neutral" in the audit queue.
  [DOCUMENT_STATUS.INFECTED]: 'rejected',
};

const SUBSCRIPTION_TONE: Record<SubscriptionStatus, StatusTone> = {
  // Bought but not yet paid: the checkout has not confirmed.
  [SUBSCRIPTION_STATUS.PENDING]: 'pending',
  [SUBSCRIPTION_STATUS.ACTIVE]: 'approved',
  [SUBSCRIPTION_STATUS.GRACE]: 'pending',
  [SUBSCRIPTION_STATUS.EXPIRED]: 'rejected',
  [SUBSCRIPTION_STATUS.CANCELLED]: 'neutral',
};

/**
 * A ticket waiting on the team reads as pending work, like a queue item; one the team
 * has answered reads as done from this side, and a closed one is simply over.
 */
const SUPPORT_TICKET_TONE: Record<SupportTicketStatus, StatusTone> = {
  [SUPPORT_TICKET_STATUS.OPEN]: 'pending',
  [SUPPORT_TICKET_STATUS.ANSWERED]: 'approved',
  [SUPPORT_TICKET_STATUS.CLOSED]: 'neutral',
};

export function verificationTone(status: VerificationStatus): StatusTone {
  return VERIFICATION_TONE[status];
}

/** Takes the wire string as well: a detail payload's document status is not narrowed. */
export function documentTone(status: DocumentStatus | (string & {})): StatusTone {
  return (DOCUMENT_TONE as Record<string, StatusTone>)[status] ?? 'neutral';
}

export function subscriptionTone(status: SubscriptionStatus): StatusTone {
  return SUBSCRIPTION_TONE[status];
}

export function supportTicketTone(status: SupportTicketStatus): StatusTone {
  return SUPPORT_TICKET_TONE[status];
}

/**
 * The statuses below arrive as plain strings on the detail and ledger payloads, and the
 * API may add a value before the console knows it. Each map is keyed by the wire value
 * and an unknown one reads as neutral — never as a colour that claims something.
 */
function toneFrom(map: Readonly<Record<string, StatusTone>>, status: string): StatusTone {
  return map[status] ?? 'neutral';
}

const ACCOUNT_TONE: Record<string, StatusTone> = {
  active: 'approved',
  suspended: 'rejected',
  deleted: 'neutral',
};

/** A talent's account: active, suspended, or deleted. */
export function accountTone(status: string): StatusTone {
  return toneFrom(ACCOUNT_TONE, status);
}

const CREDENTIAL_TONE: Record<string, StatusTone> = {
  listed: 'approved',
  awaiting_audit: 'pending',
  removed: 'rejected',
};

/** A credential on a talent's profile: listed, waiting for audit, or removed. */
export function credentialTone(status: string): StatusTone {
  return toneFrom(CREDENTIAL_TONE, status);
}

const MEMBER_TONE: Record<string, StatusTone> = {
  active: 'approved',
  invited: 'pending',
  disabled: 'rejected',
  revoked: 'neutral',
};

/** A member of an organization's roster. */
export function memberTone(status: string): StatusTone {
  return toneFrom(MEMBER_TONE, status);
}

const PAYMENT_TONE: Record<string, StatusTone> = {
  paid: 'approved',
  pending: 'pending',
  refunded: 'rejected',
};

/** A payment in the ledger. `initiated` and `failed` read as neutral, as they always have. */
export function paymentTone(status: string): StatusTone {
  return toneFrom(PAYMENT_TONE, status);
}

/**
 * A talent's plan subscription. `pending` is a checkout not yet confirmed — waiting, like
 * a grace period — and is not a `SubscriptionStatus`, so it is resolved first.
 */
export function planSubscriptionTone(status: string): StatusTone {
  if (status === 'pending') return 'pending';
  return toneFrom(SUBSCRIPTION_TONE, status);
}

/**
 * An organization's badge state, or its registration's: the registration says
 * `approved` where the organization says `verified`.
 */
export function organizationStatusTone(status: string): StatusTone {
  return toneFrom(VERIFICATION_TONE, status === 'approved' ? VERIFICATION_STATUS.VERIFIED : status);
}
